import React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { SortFieldType } from "./sortUtils";

interface BuildDocumentLinkColumnProps<T> {
  accessorKey: string;
  header: string;
  getDocumentUrl: (row: T) => string | null | undefined;
  getDocumentName: (row: T) => string | null | undefined;
  onDocumentClick?: (row: T, url: string) => void;
  size?: number;
  minSize?: number;
  sortType?: SortFieldType;
  enableSorting?: boolean;
  meta?: Record<string, any>;
}

/**
 * Helper to create a column that displays clickable document links.
 * By default, clicking the link opens the document in a new tab.
 * The link click stops event propagation so the row click handler doesn't fire.
 *
 * @example
 * buildDocumentLinkColumn({
 *   accessorKey: "documentName",
 *   header: "Document",
 *   getDocumentUrl: (row) => row.documentUrl,
 *   getDocumentName: (row) => row.documentName,
 *   size: 300
 * })
 *
 * @example
 * // With custom click handler
 * buildDocumentLinkColumn({
 *   accessorKey: "documentName",
 *   header: "Document",
 *   getDocumentUrl: (row) => row.documentUrl,
 *   getDocumentName: (row) => row.documentName,
 *   onDocumentClick: (row, url) => {
 *     // Custom logic before opening
 *     console.log("Opening document:", url);
 *     window.open(url, "_blank");
 *   },
 *   size: 300
 * })
 */
export function buildDocumentLinkColumn<T>({
  accessorKey,
  header,
  getDocumentUrl,
  getDocumentName,
  onDocumentClick,
  size,
  minSize,
  sortType,
  enableSorting,
  meta
}: BuildDocumentLinkColumnProps<T>): ColumnDef<T> {
  const CellComponent = ({ row }: { row: Row<T> }) => {
    const documentUrl = getDocumentUrl(row.original);
    const documentName = getDocumentName(row.original);

    if (!documentUrl || !documentName) {
      return <span className="opacity-20">--</span>;
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (onDocumentClick) {
        onDocumentClick(row.original, documentUrl);
      } else {
        window.open(documentUrl, "_blank", "noopener,noreferrer");
      }
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        className="text-left underline text-sys-brand-primary hover:text-sys-brand-primary-dark cursor-pointer"
      >
        {documentName}
      </button>
    );
  };

  return {
    accessorKey,
    header,
    ...(size && { size }),
    ...(minSize && { minSize: minSize ?? size }),
    ...(enableSorting !== undefined && { enableSorting }),
    meta: {
      ...(sortType && { sortType }),
      ...meta
    },
    cell: CellComponent
  };
}
